// =============================================================================
// PEDRA — src/optimizer/geometry.ts
// Retangulos, areas e a REGRA DO KERF.
//
// Sistema de coordenadas (igual ao SVG): origem no canto SUPERIOR-ESQUERDO.
//   x -> direita, mede a LARGURA (width)
//   y -> baixo,   mede o COMPRIMENTO (length)
// Tudo em Micron (inteiro, 1/1000 mm).
// =============================================================================

import type { Micron, Micron2, Rect } from '../core/types';

// -----------------------------------------------------------------------------
// CONSTRUCAO E MEDIDAS
// -----------------------------------------------------------------------------

/** Cria um retangulo. Dimensoes negativas sao normalizadas para zero. */
export function makeRect(x: Micron, y: Micron, width: Micron, length: Micron): Rect {
  return { x, y, width: width > 0 ? width : 0, length: length > 0 ? length : 0 };
}

/** Copia rasa de um retangulo (nunca compartilhe referencias entre chapas). */
export function cloneRect(r: Rect): Rect {
  return { x: r.x, y: r.y, width: r.width, length: r.length };
}

/** Area em µm². */
export function rectArea(r: Rect): Micron2 {
  return r.width * r.length;
}

/** Coordenada X da borda direita. */
export function rectRight(r: Rect): Micron {
  return r.x + r.width;
}

/** Coordenada Y da borda inferior. */
export function rectBottom(r: Rect): Micron {
  return r.y + r.length;
}

/** Menor lado do retangulo. */
export function rectShortSide(r: Rect): Micron {
  return r.width < r.length ? r.width : r.length;
}

/** Maior lado do retangulo. */
export function rectLongSide(r: Rect): Micron {
  return r.width > r.length ? r.width : r.length;
}

/** true se uma peca `width x length` cabe (com folga zero) dentro de `r`. */
export function fitsIn(width: Micron, length: Micron, r: Rect): boolean {
  return width > 0 && length > 0 && width <= r.width && length <= r.length;
}

/** true se `inner` esta inteiramente dentro de `outer` (bordas coincidentes valem). */
export function containsRect(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    rectRight(inner) <= rectRight(outer) &&
    rectBottom(inner) <= rectBottom(outer)
  );
}

/** true se `a` e `b` se sobrepoem com AREA POSITIVA (encostar nao conta). */
export function intersectsRect(a: Rect, b: Rect): boolean {
  return (
    a.x < rectRight(b) &&
    b.x < rectRight(a) &&
    a.y < rectBottom(b) &&
    b.y < rectBottom(a)
  );
}

/** Retangulo de interseccao, ou `null` quando a area comum e zero. */
export function intersectionRect(a: Rect, b: Rect): Rect | null {
  const x = a.x > b.x ? a.x : b.x;
  const y = a.y > b.y ? a.y : b.y;
  const r = Math.min(rectRight(a), rectRight(b));
  const bo = Math.min(rectBottom(a), rectBottom(b));
  if (r <= x || bo <= y) return null;
  return { x, y, width: r - x, length: bo - y };
}

/** Comprimento da sobreposicao entre os intervalos [a0,a1) e [b0,b1). */
export function overlapLength(a0: Micron, a1: Micron, b0: Micron, b1: Micron): Micron {
  const lo = a0 > b0 ? a0 : b0;
  const hi = a1 < b1 ? a1 : b1;
  return hi > lo ? hi - lo : 0;
}

/**
 * Sobra reaproveitavel: os DOIS lados precisam alcancar `minTrim`.
 * Com `minTrim === 0` qualquer retangulo de area positiva e aproveitavel.
 */
export function isUsableOffcut(r: Rect, minTrim: Micron): boolean {
  if (r.width <= 0 || r.length <= 0) return false;
  const min = Number.isFinite(minTrim) && minTrim > 0 ? minTrim : 0;
  return r.width >= min && r.length >= min;
}

// -----------------------------------------------------------------------------
// REGRA DO KERF — o coracao da correcao do plano de corte
// -----------------------------------------------------------------------------

/**
 * Sobras de um posicionamento, ja descontando a lamina.
 *
 * `rxRaw`/`ryRaw` — sobra geometrica bruta, ANTES da serra.
 * `kx`/`ky`       — material destruido pela lamina naquele eixo.
 * `rx`/`ry`       — sobra UTIL, o que realmente pode receber outra peca.
 */
export interface Residuals {
  rxRaw: Micron;
  ryRaw: Micron;
  kx: Micron;
  ky: Micron;
  rx: Micron;
  ry: Micron;
}

/**
 * REGRA DO KERF (critica, nao negociar):
 *
 *   rxRaw = free.width - w
 *   - `rxRaw === 0` -> NAO existe corte naquele eixo; custo zero.
 *   - `rxRaw > 0`   -> EXISTE corte e ele custa `kerf`, mesmo quando
 *                      `rxRaw < kerf` (a lamina consome a tira inteira e a
 *                      sobra util fica em 0).
 *
 * Consequencia direta: duas pecas vizinhas ficam separadas por EXATAMENTE
 * um kerf. O desconto acontece aqui, na criacao de cada filho — nunca no fim.
 */
export function residuals(free: Rect, w: Micron, l: Micron, kerf: Micron): Residuals {
  const k = Number.isFinite(kerf) && kerf > 0 ? kerf : 0;
  const rxRaw = free.width - w;
  const ryRaw = free.length - l;
  const kx = rxRaw > 0 ? k : 0;
  const ky = ryRaw > 0 ? k : 0;
  const rx = rxRaw - kx;
  const ry = ryRaw - ky;
  return {
    rxRaw,
    ryRaw,
    kx,
    ky,
    rx: rx > 0 ? rx : 0,
    ry: ry > 0 ? ry : 0,
  };
}

// -----------------------------------------------------------------------------
// MERGE DE AREAS LIVRES
// -----------------------------------------------------------------------------

/**
 * Junta pares de retangulos livres que sao ADJACENTES e perfeitamente
 * alinhados (mesma faixa em x ou em y, encostados sem folga). O resultado
 * continua sendo uma particao valida — por isso nao quebra a propriedade de
 * guilhotina de quem consome a lista.
 *
 * Roda ate o ponto fixo, com teto de passadas para nunca travar.
 *
 * INVARIANTE: a area total NUNCA muda (fora os degenerados descartados na
 * entrada). `joinRects` so devolve um retangulo cuja area e exatamente a soma
 * das duas parcelas, e cada juncao remove o parceiro da lista — por isso e
 * obrigatorio reler `out[i]` a cada comparacao: usar uma copia capturada antes
 * do laco interno faria a segunda juncao da mesma passada sobrescrever o
 * retangulo ja crescido, apagando a area do primeiro parceiro.
 */
export function mergeFreeRects(rects: readonly Rect[]): Rect[] {
  const out: Rect[] = rects.map(cloneRect).filter((r) => r.width > 0 && r.length > 0);
  const maxPasses = 8;
  for (let pass = 0; pass < maxPasses; pass++) {
    let merged = false;
    for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        // `out[i]` e relido AQUI de proposito: ele cresce a cada juncao.
        const joined = joinRects(out[i] as Rect, out[j] as Rect);
        if (joined === null) continue;
        out[i] = joined;
        out.splice(j, 1);
        merged = true;
        j = i; // reinicia a varredura interna com o retangulo ja crescido
      }
    }
    if (!merged) break;
  }
  return out;
}

/** Une dois retangulos adjacentes e alinhados; `null` se nao formam um retangulo. */
function joinRects(a: Rect, b: Rect): Rect | null {
  // Lado a lado no eixo X (mesma faixa vertical).
  if (a.y === b.y && a.length === b.length) {
    if (rectRight(a) === b.x) return { x: a.x, y: a.y, width: a.width + b.width, length: a.length };
    if (rectRight(b) === a.x) return { x: b.x, y: b.y, width: a.width + b.width, length: a.length };
  }
  // Empilhados no eixo Y (mesma faixa horizontal).
  if (a.x === b.x && a.width === b.width) {
    if (rectBottom(a) === b.y) return { x: a.x, y: a.y, width: a.width, length: a.length + b.length };
    if (rectBottom(b) === a.y) return { x: b.x, y: b.y, width: a.width, length: a.length + b.length };
  }
  return null;
}

/**
 * Remove retangulos contidos em outros da mesma lista (mantem so os maximais).
 * Usado no modo livre, onde os retangulos livres podem se sobrepor.
 */
export function dropContainedRects(rects: readonly Rect[]): Rect[] {
  const src = rects.filter((r) => r.width > 0 && r.length > 0);
  const out: Rect[] = [];
  for (let i = 0; i < src.length; i++) {
    const a = src[i] as Rect;
    let dominated = false;
    for (let j = 0; j < src.length; j++) {
      if (i === j) continue;
      const b = src[j] as Rect;
      if (!containsRect(b, a)) continue;
      // Em caso de retangulos identicos, mantem apenas o de menor indice.
      if (rectArea(b) > rectArea(a) || j < i) {
        dominated = true;
        break;
      }
    }
    if (!dominated) out.push(cloneRect(a));
  }
  return out;
}

/** Ordenacao canonica (y, x, largura, comprimento) — usada para determinismo. */
export function compareRects(a: Rect, b: Rect): number {
  if (a.y !== b.y) return a.y - b.y;
  if (a.x !== b.x) return a.x - b.x;
  if (a.width !== b.width) return a.width - b.width;
  return a.length - b.length;
}
